import { toast } from "react-toastify";
import { apiSlice } from ".";

/**
 * Agent WhatsApp — the second WhatsApp system that runs alongside the Agency
 * WhatsApp onboarding (`/api/whatsapp/onboarding`).
 *
 * All endpoints authenticate with the agent token + agent id.
 */

export const agentWhatsappApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // ---------- Agent connection ----------
    getAgentWhatsappStatus: builder.query({
      query: () => "/api/agentwhatsapp/status",
      providesTags: ["AgentWhatsapp"],
    }),

    // Exchanges the FB.login `code` from WhatsApp Embedded Signup for tokens
    // on the backend. Auth + userid headers are added by the base query.
    completeAgentWhatsappOnboarding: builder.mutation({
      query: ({ code }) => ({
        url: "/api/agentwhatsapp/complete",
        method: "POST",
        body: { code },
      }),
      invalidatesTags: ["AgentWhatsapp"],
    }),

    disconnectAgentWhatsapp: builder.mutation({
      query: () => ({
        url: "/api/agentwhatsapp/disconnect",
        method: "POST",
      }),
      async onQueryStarted(args, { queryFulfilled }) {
        toast.promise(queryFulfilled, {
          pending: "Disconnecting WhatsApp",
          success: "WhatsApp disconnected successfully",
          error: "Error Disconnecting WhatsApp",
        });
      },
      invalidatesTags: ["AgentWhatsapp", "AgentWhatsappConversations"],
    }),

    toggleAgentWhatsapp: builder.mutation({
      query: () => ({
        url: "/api/agentwhatsapp/toggle",
        method: "PUT",
      }),
      async onQueryStarted(args, { queryFulfilled }) {
        toast.promise(queryFulfilled, {
          pending: "Updating Automation",
          success: "Automation Preference Updated",
          error: "Error Updating Automation",
        });
      },
      invalidatesTags: ["AgentWhatsapp"],
    }),

    // ---------- Agent inbox ----------
    getAgentWhatsappConversations: builder.query({
      query: () => "/api/agentwhatsapp/conversations",
      providesTags: ["AgentWhatsappConversations"],
    }),

    getAgentWhatsappConversation: builder.query({
      query: ({ conversationId }) =>
        `/api/agentwhatsapp/conversation/${conversationId}`,
      providesTags: (result, error, { conversationId }) => [
        { type: "AgentWhatsappConversation", id: conversationId },
      ],
    }),

    sendAgentWhatsappMessage: builder.mutation({
      query: ({ conversationId, message }) => ({
        url: `/api/agentwhatsapp/send/${conversationId}`,
        method: "POST",
        body: { message },
      }),
      invalidatesTags: (result, error, { conversationId }) => [
        { type: "AgentWhatsappConversation", id: conversationId },
        "AgentWhatsappConversations",
      ],
    }),

  }),
});

export const {
  useGetAgentWhatsappStatusQuery,
  useCompleteAgentWhatsappOnboardingMutation,
  useDisconnectAgentWhatsappMutation,
  useToggleAgentWhatsappMutation,
  useGetAgentWhatsappConversationsQuery,
  useGetAgentWhatsappConversationQuery,
  useSendAgentWhatsappMessageMutation,
} = agentWhatsappApi;
